import React from "react";
import clsx from "clsx";
import { useHistory } from "react-router-dom";
import {
  Badge,
  Button,
  ButtonVariant,
  CopyIcon,
  Text,
  Tooltip,
} from "@merge-api/merge-javascript-shared";
import { ChevronRight, Trash, Users } from "lucide-react";
import {
  APICategory,
  AuthType,
  LinkedAccount,
  PauseReason,
  UnreleasedAPICategory,
  UserType,
} from "../../../../../../../../models/Entities";
import {
  getLinkedAccountDetailOverviewPathForId,
  openInNewTab,
} from "../../../../../../../../router/RouterUtils";
import { getFormattedEventDistanceFromNow } from "../../../../../../utils/IntegrationsManagementUtils";
import { displayNameForAPICategoryIntegration } from "../../../../../../../../models/Helpers";
import { conditionallyPluralize } from "../../../../../../../../utils/general";
import IntegrationNameAndLogo from "../../../../../../../shared/IntegrationNameAndLogo";
import useAppContext from "../../../../../../../context/useAppContext";
import StatusBadge from "./components/StatusBadge";

interface Props {
  linkedAccount: LinkedAccount;
  onDeleteClick: (linkedAccount: LinkedAccount) => void;
  showDataFreshAsOf: boolean;
}

const LinkedAccountRow = ({ linkedAccount, onDeleteClick, showDataFreshAsOf }: Props) => {
  const history = useHistory();
  const { user } = useAppContext();

  const isSupportRole = user.type === UserType.support;
  const isPlanLimitReached = linkedAccount.pause_reason === PauseReason.PLAN_LIMIT_REACHED;

  const detailPath = getLinkedAccountDetailOverviewPathForId(linkedAccount.id);

  const onRowClick = (event: React.MouseEvent) => {
    if (event.metaKey || event.ctrlKey) {
      openInNewTab(detailPath);
    } else {
      history.push(detailPath);
    }
  };

  const onRowAuxClick = (event: React.MouseEvent) => {
    if (event.button === 1) {
      openInNewTab(detailPath);
    }
  };

  const onDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    onDeleteClick(linkedAccount);
  };

  const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

  return (
    <tr
      className={clsx(
        "border-t border-gray-10 cursor-pointer hover:bg-gray-0",
        isPlanLimitReached && "text-gray-40",
      )}
      onClick={onRowClick}
      onAuxClick={onRowAuxClick}
    >
      <td className="px-3 py-2 align-top">
        <div className="flex w-80 flex-col">
          <div className="flex flex-row items-center gap-1.5 min-w-0">
            {linkedAccount.is_duplicate && (
              <Tooltip title="Duplicate Linked Account detected">
                <Users size={16} className="text-yellow-70 shrink-0" />
              </Tooltip>
            )}
            {/* Organization name is customer-chosen and is thus masked at the cell */}
            <div className="ph-no-capture truncate">
              <Text>{linkedAccount.end_user.organization_name}</Text>
            </div>
          </div>
          {linkedAccount.id && (
            <div className="flex flex-row items-center gap-2 min-w-0">
              <div className="shrink-0" onClick={stopPropagation} onAuxClick={stopPropagation}>
                <CopyIcon size={12} text={linkedAccount.id} className="text-gray-40" />
              </div>
              <div className="ph-no-capture truncate">
                <Text variant="sm" className="text-gray-40">
                  {linkedAccount.id}
                </Text>
              </div>
            </div>
          )}
        </div>
      </td>
      <td className="px-3 py-2 align-top">
        <div className="flex w-56 flex-col">
          <div className="flex flex-row items-center gap-2 min-w-0">
            <IntegrationNameAndLogo integration={linkedAccount.integration} />
            {linkedAccount.auth_type === AuthType.SFTP && (
              <Badge className="items-center gap-1" color="purple">
                SFTP
              </Badge>
            )}
          </div>
          <div className="text-gray-40">
            {displayNameForAPICategoryIntegration(
              linkedAccount.category as APICategory | UnreleasedAPICategory,
              "sm",
              12,
            )}
          </div>
        </div>
      </td>
      <td className="px-3 py-2 align-top">
        <div className="flex flex-row items-center gap-1.5">
          <StatusBadge linkedAccount={linkedAccount} />
          {linkedAccount.open_issue_count > 0 ? (
            <Badge color="red" size="sm" className="whitespace-nowrap">
              {conditionallyPluralize("issue", linkedAccount.open_issue_count)}
            </Badge>
          ) : (
            <Badge color="gray" size="sm" className="whitespace-nowrap">
              No issues
            </Badge>
          )}
        </div>
      </td>
      <td className="px-3 py-2 align-top whitespace-nowrap">
        <Text className="text-gray-50">
          {linkedAccount.completed_at
            ? getFormattedEventDistanceFromNow(linkedAccount.completed_at)
            : "-"}
        </Text>
      </td>
      {showDataFreshAsOf && (
        <td className="px-3 py-2 align-top whitespace-nowrap">
          <Text className="text-gray-50">
            {linkedAccount.data_fresh_as_of
              ? getFormattedEventDistanceFromNow(linkedAccount.data_fresh_as_of)
              : "-"}
          </Text>
        </td>
      )}
      <td className="px-3 py-2 align-middle">
        <div className="flex flex-row items-center justify-end gap-3">
          <Tooltip title={isSupportRole ? "Support users cannot delete" : "Delete Linked Account"}>
            <Button
              variant={ButtonVariant.IconOnly}
              size="sm"
              aria-label="Delete Linked Account"
              disabled={isSupportRole}
              onClick={onDelete}
              onAuxClick={stopPropagation}
            >
              <Trash size={12} />
            </Button>
          </Tooltip>
          <Tooltip title="Review Linked Account details">
            <ChevronRight size={16} className="text-gray-40" />
          </Tooltip>
        </div>
      </td>
    </tr>
  );
};

export default LinkedAccountRow;
